/**
 * /app/$tenantSlug/attendance/monthly-summary?month=YYYY-MM — the month HR
 * checks before locking payroll (persona round 2026-10).
 *
 * One line per employee in the month's payroll: days present, paid leave,
 * loss-of-pay days (unpaid leave + absences when attendance drives LOP),
 * WFH, holidays and weekly offs, with employee code and department. The
 * server computes it with the payroll run's own input code, so the LOP
 * shown here is the LOP the payroll run uses (before any manual change on
 * the run). "Download CSV" gives the same table as a spreadsheet.
 *
 * HR admins only (company_admin, hr_manager) — server-gated too. The
 * payroll run page links here as /attendance/monthly-summary?month=YYYY-MM;
 * keep that URL contract.
 */

import { useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { CalendarDays, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { attendanceApi } from "@/lib/api";
import { todayISO } from "@/lib/leave-format";
import { downloadBlob } from "@/lib/bulk-import-format";
import { monthLabel } from "@/lib/attendance-team";
import { fmtDays, parseMonthParam, shiftMonth } from "@/lib/attendance-summary";

const searchSchema = z.object({
  month: z.string().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/monthly-summary",
)({
  validateSearch: searchSchema,
  component: MonthlySummaryRoute,
});

function MonthlySummaryRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = useAuth();
  const allowed = isHRAdmin(user?.role);
  const month = parseMonthParam(search.month) ?? todayISO().slice(0, 7);
  const [exporting, setExporting] = useState(false);

  const summary = useQuery({
    queryKey: ["attendance", "monthly-summary", month] as const,
    queryFn: ({ signal }) => attendanceApi.getMonthlySummary(month, signal),
    enabled: allowed,
  });

  function setMonth(next: string | null) {
    if (!next) return;
    void navigate({
      to: "/app/$tenantSlug/attendance/monthly-summary",
      params: { tenantSlug },
      search: { month: next },
      replace: true,
    });
  }

  async function onDownload() {
    setExporting(true);
    try {
      const blob = await attendanceApi.exportMonthlySummary(month);
      downloadBlob(blob, `attendance-summary-${month}.csv`);
    } catch {
      toast.error("Could not download the summary");
    } finally {
      setExporting(false);
    }
  }

  if (!allowed) {
    return (
      <TimeAndLeaveShell title="Monthly Attendance Summary">
        <EmptyState
          icon={CalendarDays}
          title="Only HR can see the monthly attendance summary"
          description="Ask your company admin or HR manager if you need these numbers."
        />
      </TimeAndLeaveShell>
    );
  }

  const data = summary.data;
  const rows = data?.rows ?? [];

  return (
    <TimeAndLeaveShell
      title="Monthly Attendance Summary"
      description="Check each person's month before you lock payroll."
      actions={
        <div className="flex flex-wrap items-end gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Previous month"
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <Label htmlFor="summary-month">Month</Label>
            <Input
              id="summary-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(parseMonthParam(e.target.value))}
              className="w-44"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Next month"
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={onDownload}
            disabled={exporting || rows.length === 0}
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download CSV
          </Button>
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>{monthLabel(month)}</CardTitle>
          {data && (
            <p className="text-sm text-muted-foreground">
              {data.attendance_drives_lop
                ? "Loss of pay = unpaid leave + days absent without leave or WFH."
                : "Loss of pay = unpaid leave only. Absences don't reduce pay — you can change this in attendance settings."}{" "}
              These are the numbers the payroll run uses, before any change you
              make on the run itself.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {summary.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : summary.isError ? (
            <Alert variant="destructive">
              <AlertDescription>
                Couldn't load the summary for {monthLabel(month)}. Try again in
                a moment.
              </AlertDescription>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title={`No one is on payroll for ${monthLabel(month)}`}
              description="People appear here for every month they were employed."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Present</TableHead>
                    <TableHead className="text-right">Paid Leave</TableHead>
                    <TableHead className="text-right">Loss of Pay</TableHead>
                    <TableHead className="text-right">WFH</TableHead>
                    <TableHead className="text-right">Holidays</TableHead>
                    <TableHead className="text-right">Weekly Offs</TableHead>
                    <TableHead className="text-right">Days Employed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.employee_id}>
                      <TableCell>
                        <div className="font-medium">{r.name}</div>
                        {r.employee_code && (
                          <div className="text-xs text-muted-foreground">
                            {r.employee_code}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{r.department || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtDays(r.present_days)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtDays(r.paid_leave_days)}
                      </TableCell>
                      <TableCell
                        className={
                          r.lop_days > 0
                            ? "text-right font-medium tabular-nums text-amber-700"
                            : "text-right tabular-nums"
                        }
                        title={`Unpaid leave ${fmtDays(r.unpaid_leave_days)} + absent ${fmtDays(r.absent_lop_days)}`}
                      >
                        {fmtDays(r.lop_days)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtDays(r.wfh_days)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.holidays}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.weekly_offs}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.employed_days} of {data?.days_in_month}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </TimeAndLeaveShell>
  );
}
